import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaHandshake, FaTrash } from 'react-icons/fa';
import {
  TbHome,
  TbPhoto,
  TbChartBar,
  TbChecklist,
  TbCalendarEvent,
  TbClick,
  TbEye,
  TbExternalLink,
  TbLink,
  TbPlayerPause,
  TbPlayerPlay,
} from 'react-icons/tb';
import { twMerge } from 'tailwind-merge';

import classNames from 'classnames';
import PageHeader from '../../components/misc/PageHeader';
import PageWithTabsLayout from '../../components/layouts/PageWithTabsLayout';
import Input from '../../components/misc/Input';
import Labeled from '../../components/misc/Labeled';
import ImageInput from '../../components/forms/ImageInput';
import SearchBar from '../../components/forms/SearchBar';
import CancelSaveButton from '../../components/misc/CancelSaveButton';
import PreferencesToggle from '../../components/misc/PreferenceToggle';
import Button from '../../components/buttons/Button';
import PrimaryButton from '../../components/buttons/PrimaryButton';
import AreYouSureModal from '../../components/modals/AreYouSureModal';
import ApiImage from '../../components/misc/ApiImage';
import EmptyScreenView from '../../components/misc/EmptyScreenView';
import SponsorCampaignTypeBadge from '../../components/misc/SponsorCampaignTypeBadge';
import SponsorStatusBadge from '../../components/misc/SponsorStatusBadge';
import useSelectedBusiness from '../../../hooks/business/useSelectedBusiness';
import sponsorService from '../../../services/business/sponsorService';
import {
  SponsorCampaignType,
  SponsorDetail,
  SponsorLinkableEntities,
} from '../../../types/sponsor';
import {
  CommunityFeatures,
  Feature,
  getBusinessSettingsFeatures,
} from '../../../utils/FeatureUtils';
import { BusinessRole } from '../../../types/business';

type SponsorDetailsTab = 'properties' | 'placements' | 'links';
type LinkSectionKey = 'events' | 'photoAlbums' | 'scoreCards' | 'tasks';

function matchesSearch(text: string, query: string): boolean {
  return text.toLowerCase().includes(query.trim().toLowerCase());
}

export default function SponsorDetailsPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sponsorId: sponsorIdParam } = useParams<{ sponsorId: string }>();
  const sponsorId = Number(sponsorIdParam);
  const business = useSelectedBusiness();
  const features = getBusinessSettingsFeatures(business) as CommunityFeatures;
  const canManage =
    business?.role === BusinessRole.OWNER || business?.role === BusinessRole.MANAGER;
  const sponsorsEnabled = features[Feature.SPONSORS] === true;

  const [sponsor, setSponsor] = useState<SponsorDetail | null>(null);
  const [linkable, setLinkable] = useState<SponsorLinkableEntities | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<SponsorDetailsTab>('properties');
  const [savingProperties, setSavingProperties] = useState(false);
  const [savingPlacements, setSavingPlacements] = useState(false);
  const [savingLinks, setSavingLinks] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [togglingActive, setTogglingActive] = useState(false);

  const [name, setName] = useState('');
  const [campaignType, setCampaignType] = useState<SponsorCampaignType>('COMMUNITY');
  const [active, setActive] = useState(true);
  const [description, setDescription] = useState('');
  const [url, setUrl] = useState('');
  const [logoPreview, setLogoPreview] = useState<string | undefined>(undefined);
  const [logoFile, setLogoFile] = useState<File | undefined>(undefined);
  const [logoAction, setLogoAction] = useState<'UPLOAD' | 'DELETE' | undefined>(undefined);

  const [showOnHome, setShowOnHome] = useState(true);
  const [showOnEvents, setShowOnEvents] = useState(false);
  const [showOnPhotoAlbums, setShowOnPhotoAlbums] = useState(false);
  const [showOnScoreCards, setShowOnScoreCards] = useState(false);
  const [showOnTasks, setShowOnTasks] = useState(false);

  const [eventIds, setEventIds] = useState<number[]>([]);
  const [photoAlbumIds, setPhotoAlbumIds] = useState<string[]>([]);
  const [scoreCardIds, setScoreCardIds] = useState<number[]>([]);
  const [taskIds, setTaskIds] = useState<number[]>([]);
  const [linkSearch, setLinkSearch] = useState('');

  const showEventsPlacement = features[Feature.EVENTS] === true;
  const showPhotoAlbumsPlacement = features[Feature.PHOTO_ALBUMS] === true;
  const showScoreCardsPlacement = features[Feature.STATS_AND_SCORES] === true;
  const showTasksPlacement = features[Feature.TASKS] === true;

  const linkedCount = eventIds.length + photoAlbumIds.length + scoreCardIds.length + taskIds.length;

  const applySponsorState = (detail: SponsorDetail): void => {
    setSponsor(detail);
    setName(detail.name);
    setCampaignType(detail.campaignType);
    setActive(detail.active);
    setDescription(detail.description ?? '');
    setUrl(detail.url ?? '');
    setLogoPreview(detail.logo ?? undefined);
    setShowOnHome(detail.showOnHome);
    setShowOnEvents(detail.showOnEvents);
    setShowOnPhotoAlbums(detail.showOnPhotoAlbums);
    setShowOnScoreCards(detail.showOnScoreCards);
    setShowOnTasks(detail.showOnTasks);
    setEventIds(detail.eventIds);
    setPhotoAlbumIds(detail.photoAlbumIds);
    setScoreCardIds(detail.scoreCardIds);
    setTaskIds(detail.taskIds);
  };

  useEffect(() => {
    let cancelled = false;

    if (!Number.isFinite(sponsorId) || sponsorId <= 0) {
      setLoading(false);
    } else {
      setLoading(true);
      Promise.all([sponsorService.getSponsor(sponsorId), sponsorService.getLinkableEntities()])
        .then(([detail, entities]) => {
          if (cancelled) return;
          setLinkable(entities);
          applySponsorState(detail);
          setLogoFile(undefined);
          setLogoAction(undefined);
          setActiveTab('properties');
        })
        .catch(() => {
          if (!cancelled) {
            setSponsor(null);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [sponsorId]);

  const filteredLinkable = useMemo(() => {
    if (!linkable) {
      return { events: [], photoAlbums: [], scoreCards: [], tasks: [] };
    }
    const q = linkSearch;
    return {
      events: linkable.events.filter(
        (e) => matchesSearch(e.title, q) || matchesSearch(e.communityName, q),
      ),
      photoAlbums: linkable.photoAlbums.filter(
        (a) => matchesSearch(a.title, q) || matchesSearch(a.communityName, q),
      ),
      scoreCards: linkable.scoreCards.filter(
        (c) => matchesSearch(c.name, q) || matchesSearch(c.communityName, q),
      ),
      tasks: linkable.tasks.filter(
        (task) => matchesSearch(task.name, q) || matchesSearch(task.communityName, q),
      ),
    };
  }, [linkable, linkSearch]);

  const tabs = useMemo((): SponsorDetailsTab[] => {
    if (campaignType === 'FEATURE') {
      return ['properties', 'links'];
    }
    return ['properties', 'placements'];
  }, [campaignType]);

  useEffect(() => {
    if (!tabs.includes(activeTab)) {
      setActiveTab('properties');
    }
  }, [tabs, activeTab]);

  if (!sponsorsEnabled || !canManage) {
    return <Navigate to="/settings" replace />;
  }

  const handleSetImage = async (image: string | undefined): Promise<void> => {
    setLogoPreview(image);
    if (!image) {
      setLogoFile(undefined);
      return;
    }
    const blob = await fetch(image).then((res) => res.blob());
    setLogoFile(new File([blob], 'logo', { type: blob.type }));
  };

  const handleToggleActive = async (): Promise<void> => {
    setTogglingActive(true);
    try {
      const updated = await sponsorService.updateSponsor(sponsorId, {
        active: !active,
      });
      setActive(updated.active);
      setSponsor((prev) => (prev ? { ...prev, ...updated } : prev));
      toast.success(t(updated.active ? 'page.sponsors.activated' : 'page.sponsors.deactivated'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setTogglingActive(false);
    }
  };

  const handleSaveProperties = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t('component.modal.sponsor.nameRequired'));
      return;
    }

    let trimmedUrl = url.trim();
    if (trimmedUrl && !trimmedUrl.startsWith('http')) {
      trimmedUrl = `https://${trimmedUrl}`;
    }

    setSavingProperties(true);
    try {
      const updated = await sponsorService.updateSponsor(sponsorId, {
        name: trimmedName,
        url: trimmedUrl,
        description: description.trim(),
        logo: logoAction === 'UPLOAD' ? logoFile : undefined,
      });
      setSponsor((prev) => (prev ? { ...prev, ...updated } : prev));
      setUrl(updated.url ?? '');
      setLogoAction(undefined);
      setLogoFile(undefined);
      toast.success(t('page.sponsors.details.properties.saved'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setSavingProperties(false);
    }
  };

  const handleSavePlacements = async (): Promise<void> => {
    setSavingPlacements(true);
    try {
      const updated = await sponsorService.updateSponsor(sponsorId, {
        showOnHome,
        showOnEvents,
        showOnPhotoAlbums,
        showOnScoreCards,
        showOnTasks,
      });
      setSponsor((prev) => (prev ? { ...prev, ...updated } : prev));
      toast.success(t('page.sponsors.details.placements.saved'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setSavingPlacements(false);
    }
  };

  const handleSaveLinks = async (): Promise<void> => {
    setSavingLinks(true);
    try {
      const updated = await sponsorService.updateSponsorLinks(sponsorId, {
        eventIds,
        photoAlbumIds,
        scoreCardIds,
        taskIds,
      });
      applySponsorState(updated);
      toast.success(t('page.sponsors.details.links.saved'));
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setSavingLinks(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    try {
      await sponsorService.deleteSponsor(sponsorId);
      toast.success(t('component.modal.sponsor.deleted'));
      navigate('/sponsors');
    } catch {
      toast.error(t('toast.error.unexpected'));
    } finally {
      setConfirmDelete(false);
    }
  };

  const toggleId = <T extends string | number>(
    list: T[],
    id: T,
    setList: React.Dispatch<React.SetStateAction<T[]>>,
  ): void => {
    setList((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">{t('page.sponsors.details.loading')}</p>
      </div>
    );
  }

  if (!sponsor) {
    return (
      <EmptyScreenView
        title={t('page.sponsors.details.notFound')}
        subtitle={t('page.sponsors.details.notFoundHint')}
        primaryButton={
          <Button variant="tertiary" onClick={() => navigate('/sponsors')}>
            <FaArrowLeft className="mr-2 h-4 w-4" />
            {t('page.sponsors.details.back')}
          </Button>
        }
      />
    );
  }

  const resetLinks = (): void => {
    setEventIds(sponsor.eventIds);
    setPhotoAlbumIds(sponsor.photoAlbumIds);
    setScoreCardIds(sponsor.scoreCardIds);
    setTaskIds(sponsor.taskIds);
    setLinkSearch('');
  };

  const renderLinkSection = (
    key: LinkSectionKey,
    title: string,
    icon: React.ComponentType<{ className?: string }>,
  ): JSX.Element => {
    const Icon = icon;
    let items: { id: string | number; label: string; communityName: string }[] = [];
    let selected: (string | number)[] = [];
    let onToggle: (id: string | number) => void = () => undefined;

    if (key === 'events') {
      items = filteredLinkable.events.map((e) => ({
        id: e.eventId,
        label: e.title,
        communityName: e.communityName,
      }));
      selected = eventIds;
      onToggle = (id) => toggleId(eventIds, Number(id), setEventIds);
    } else if (key === 'photoAlbums') {
      items = filteredLinkable.photoAlbums.map((a) => ({
        id: a.photoAlbumId,
        label: a.title,
        communityName: a.communityName,
      }));
      selected = photoAlbumIds;
      onToggle = (id) => toggleId(photoAlbumIds, String(id), setPhotoAlbumIds);
    } else if (key === 'scoreCards') {
      items = filteredLinkable.scoreCards.map((c) => ({
        id: c.scoreCardId,
        label: c.name,
        communityName: c.communityName,
      }));
      selected = scoreCardIds;
      onToggle = (id) => toggleId(scoreCardIds, Number(id), setScoreCardIds);
    } else {
      items = filteredLinkable.tasks.map((task) => ({
        id: task.taskId,
        label: task.name,
        communityName: task.communityName,
      }));
      selected = taskIds;
      onToggle = (id) => toggleId(taskIds, Number(id), setTaskIds);
    }

    return (
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-primary-900">
            <Icon className="h-5 w-5" />
            <h3 className="text-base font-medium">{title}</h3>
          </div>
          <span className="text-primary-700 text-sm font-medium">{selected.length}</span>
        </div>

        {items.length === 0 ? (
          <p className="py-4 text-sm text-gray-500">{t('page.sponsors.details.links.empty')}</p>
        ) : (
          <div className="border-primary-200 flex max-h-56 flex-col gap-2 overflow-y-auto border-y py-3">
            {items.map((item) => {
              const checked = selected.includes(item.id as never);
              return (
                <button
                  key={String(item.id)}
                  type="button"
                  onClick={() => onToggle(item.id)}
                  className={classNames(
                    'flex w-full items-center justify-between gap-3 rounded-[8px] border border-secondary-200 px-3 py-2 text-left transition-all',
                    { 'bg-secondary-200': checked },
                  )}>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-primary-900">{item.label}</p>
                    <p className="truncate text-[13px] text-gray-500">
                      {t('page.sponsors.details.links.community')}: {item.communityName}
                    </p>
                  </div>
                  <input type="checkbox" checked={checked} readOnly />
                </button>
              );
            })}
          </div>
        )}
      </section>
    );
  };

  const renderTabContent = (tab: SponsorDetailsTab): JSX.Element => {
    if (tab === 'properties') {
      return (
        <div className="flex max-w-3xl flex-col gap-8">
          <section className="flex flex-col gap-4">
            <h2 className="text-xl font-medium text-primary-900">
              {t('page.sponsors.details.sections.sponsor')}
            </h2>
            <Input
              id="sponsor-detail-name"
              type="text"
              label={t('component.modal.sponsor.name')}
              placeholder={t('component.modal.sponsor.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Labeled label={t('component.modal.sponsor.logo')}>
              <ImageInput
                className="h-32 w-56"
                iconClassName="w-8 h-8"
                fit="contain"
                image={logoPreview}
                setImage={handleSetImage}
                setImageAction={setLogoAction}
              />
              <p className="text-sm text-gray-500">{t('component.modal.sponsor.logoHint')}</p>
            </Labeled>
            <Input
              id="sponsor-detail-description"
              type="textarea"
              label={t('component.modal.sponsor.description')}
              placeholder={t('component.modal.sponsor.descriptionPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="h-28"
              maxLength={2000}
            />
            <Input
              id="sponsor-detail-url"
              type="text"
              label={t('component.modal.sponsor.url')}
              placeholder={t('component.modal.sponsor.urlPlaceholder')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          </section>

          <div className="flex justify-end">
            <CancelSaveButton
              onCancel={() => {
                setName(sponsor.name);
                setDescription(sponsor.description ?? '');
                setUrl(sponsor.url ?? '');
                setLogoPreview(sponsor.logo ?? undefined);
                setLogoFile(undefined);
                setLogoAction(undefined);
              }}
              onSave={handleSaveProperties}
              disabled={!name.trim()}
              loading={savingProperties}
            />
          </div>
        </div>
      );
    }

    if (tab === 'placements') {
      return (
        <div className="flex max-w-2xl flex-col gap-2">
          <h2 className="mb-1 text-xl font-medium text-primary-900">
            {t('page.sponsors.details.placements.title')}
          </h2>
          <p className="mb-3 text-sm text-gray-600">
            {t('page.sponsors.details.placements.subtitle')}
          </p>

          <PreferencesToggle
            icon={TbHome}
            title={t('page.sponsors.details.placements.home')}
            subTitle={t('page.sponsors.details.placements.homeHint')}
            checked={showOnHome}
            onPress={() => setShowOnHome((v) => !v)}
          />
          {showEventsPlacement && (
            <PreferencesToggle
              icon={TbCalendarEvent}
              title={t('page.sponsors.details.placements.events')}
              subTitle={t('page.sponsors.details.placements.eventsHint')}
              checked={showOnEvents}
              onPress={() => setShowOnEvents((v) => !v)}
            />
          )}
          {showPhotoAlbumsPlacement && (
            <PreferencesToggle
              icon={TbPhoto}
              title={t('page.sponsors.details.placements.photoAlbums')}
              subTitle={t('page.sponsors.details.placements.photoAlbumsHint')}
              checked={showOnPhotoAlbums}
              onPress={() => setShowOnPhotoAlbums((v) => !v)}
            />
          )}
          {showScoreCardsPlacement && (
            <PreferencesToggle
              icon={TbChartBar}
              title={t('page.sponsors.details.placements.scoreCards')}
              subTitle={t('page.sponsors.details.placements.scoreCardsHint')}
              checked={showOnScoreCards}
              onPress={() => setShowOnScoreCards((v) => !v)}
            />
          )}
          {showTasksPlacement && (
            <PreferencesToggle
              icon={TbChecklist}
              title={t('page.sponsors.details.placements.tasks')}
              subTitle={t('page.sponsors.details.placements.tasksHint')}
              checked={showOnTasks}
              onPress={() => setShowOnTasks((v) => !v)}
            />
          )}

          <div className="mt-4 flex justify-end">
            <PrimaryButton
              text={t('general.save')}
              onClick={handleSavePlacements}
              loading={savingPlacements}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex max-w-3xl flex-col gap-6">
        <div>
          <h2 className="text-xl font-medium text-primary-900">
            {t('page.sponsors.details.links.title')}
          </h2>
          <p className="text-primary-500 mt-1 text-sm">
            {t('page.sponsors.details.links.subtitle')}
          </p>
        </div>

        <SearchBar
          search={linkSearch}
          setSearch={setLinkSearch}
          className="max-w-md"
          inputProps={{ placeholder: t('page.sponsors.details.links.search') }}
        />

        <div className="flex flex-col gap-6">
          {showEventsPlacement &&
            renderLinkSection('events', t('page.sponsors.details.links.events'), TbCalendarEvent)}
          {showPhotoAlbumsPlacement &&
            renderLinkSection('photoAlbums', t('page.sponsors.details.links.photoAlbums'), TbPhoto)}
          {showScoreCardsPlacement &&
            renderLinkSection(
              'scoreCards',
              t('page.sponsors.details.links.scoreCards'),
              TbChartBar,
            )}
          {showTasksPlacement &&
            renderLinkSection('tasks', t('page.sponsors.details.links.tasks'), TbChecklist)}
        </div>

        <div className="flex items-center justify-between gap-4">
          <p className="text-primary-700 text-sm">
            {t('page.sponsors.details.links.selectedCount', { count: linkedCount })}
          </p>
          <CancelSaveButton
            onCancel={resetLinks}
            onSave={handleSaveLinks}
            buttonText={t('page.sponsors.details.links.save')}
            loading={savingLinks}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <Button variant="tertiary" onClick={() => navigate('/sponsors')}>
          <FaArrowLeft className="size-5" />
          <span>{t('page.sponsors.details.back')}</span>
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="primary-outline" onClick={handleToggleActive} loading={togglingActive}>
            {active ? <TbPlayerPause className="h-4 w-4" /> : <TbPlayerPlay className="h-4 w-4" />}
            {t(active ? 'page.sponsors.deactivate' : 'page.sponsors.activate')}
          </Button>
          <Button variant="danger-v2" onClick={() => setConfirmDelete(true)}>
            <FaTrash className="h-3.5 w-3.5" />
            {t('page.sponsors.details.delete')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-4">
        {sponsor.logo ? (
          <ApiImage
            path={sponsor.logo}
            alt={sponsor.name}
            className={twMerge(
              'h-16 w-auto max-w-[10rem] flex-shrink-0 rounded-lg bg-secondary-50 object-contain',
              !active && 'opacity-60',
            )}
          />
        ) : (
          <div
            className={twMerge(
              'flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-lg bg-secondary-200',
              !active && 'opacity-60',
            )}>
            <FaHandshake className="text-primary-700 h-7 w-7" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <PageHeader
            className="pb-0"
            title={sponsor.name}
            badge={
              <div className="flex flex-wrap items-center gap-2">
                <SponsorCampaignTypeBadge type={campaignType} size="md" />
                <SponsorStatusBadge active={active} size="md" />
              </div>
            }
            subtitle={t('page.sponsors.details.subtitle')}
          />
        </div>
      </div>

      {!active && (
        <div className="rounded-lg border border-secondary-200 bg-secondary-200/40 px-4 py-3 text-sm text-primary-900">
          {t('page.sponsors.inactiveHint')}
        </div>
      )}

      <div className="mb-2 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-lg bg-secondary-200/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm text-primary-900/70">
            <TbEye className="h-4 w-4" />
            {t('page.sponsors.details.stats.impressions')}
          </div>
          <p className="text-2xl font-bold text-primary-900">{sponsor.impressionCount}</p>
        </div>
        <div className="rounded-lg bg-secondary-200/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm text-primary-900/70">
            <TbClick className="h-4 w-4" />
            {t('page.sponsors.details.stats.clicks')}
          </div>
          <p className="text-2xl font-bold text-primary-900">{sponsor.clickCount}</p>
        </div>
        <div className="rounded-lg bg-secondary-200/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm text-primary-900/70">
            <TbExternalLink className="h-4 w-4" />
            {t('page.sponsors.details.stats.websiteOpens')}
          </div>
          <p className="text-2xl font-bold text-primary-900">{sponsor.websiteOpenCount ?? 0}</p>
        </div>
        <div className="rounded-lg bg-secondary-200/50 p-4">
          <div className="mb-1 flex items-center gap-2 text-sm text-primary-900/70">
            <TbLink className="h-4 w-4" />
            {campaignType === 'FEATURE'
              ? t('page.sponsors.details.stats.connections')
              : t('page.sponsors.details.stats.placements')}
          </div>
          <p className="text-2xl font-bold text-primary-900">
            {campaignType === 'FEATURE'
              ? linkedCount
              : [showOnHome, showOnEvents, showOnPhotoAlbums, showOnScoreCards, showOnTasks].filter(
                  Boolean,
                ).length}
          </p>
        </div>
      </div>

      <PageWithTabsLayout
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabLabels={{
          properties: t('page.sponsors.details.tabs.properties'),
          placements: t('page.sponsors.details.tabs.placements'),
          links: t('page.sponsors.details.tabs.links'),
        }}
        renderTabContent={renderTabContent}
      />

      <AreYouSureModal
        open={confirmDelete}
        setOpen={setConfirmDelete}
        title={t('component.modal.sponsor.deleteTitle')}
        description={t('component.modal.sponsor.deleteDescription', {
          name: sponsor.name,
        })}
        onConfirm={handleDelete}
      />
    </div>
  );
}
